import { callApi } from './api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Admin-role gated server-side.
const adminApiUrl = import.meta.env.VITE_API_URL_V1 + '/admin';

export type ReportStatus = 'open' | 'reviewed' | 'action_taken' | 'dismissed';

export type ReportReason = 'spam' | 'harassment' | 'hate_speech' | 'sexual_content' | 'violence' | 'self_harm' | 'other';

/**
 * One member's report of one chat message.
 *
 * `messageSnapshot` is a COPY taken at report time, not a pointer: the sender
 * can edit or delete the message afterwards, and a report that no longer says
 * what was reported cannot be triaged.
 */
export interface ContentReport {
    id: string;
    created_at: string;
    updated_at: string;
    reporterId: string;
    reportedUserId: string;
    chatId: string;
    messageId: string;
    reason: ReportReason;
    notes?: string;
    messageSnapshot: string;
    status: ReportStatus;
}

export interface ContentReportsPage {
    reports: ContentReport[];
    /** Null on the last page — a client that keeps paging on null loops forever. */
    nextCursor: string | null;
}

export const getContentReports = async (params: { status?: ReportStatus; limit?: number; startAfter?: string }) => {
    const search = new URLSearchParams();
    if (params.status) search.set('status', params.status);
    if (params.limit) search.set('limit', String(params.limit));
    if (params.startAfter) search.set('startAfter', params.startAfter);
    const qs = search.toString();
    return callApi<ContentReportsPage>({ url: `${adminApiUrl}/content-reports${qs ? `?${qs}` : ''}`, method: 'GET' });
};

export const useContentReports = (params: { status?: ReportStatus; startAfter?: string }) =>
    useQuery({
        queryKey: ['content-reports', params.status ?? 'all', params.startAfter ?? 'first'],
        queryFn: () => getContentReports({ ...params, limit: 50 }),
    });

/** Status is the only mutable field — the rest of the report is evidence. */
export const updateContentReportStatus = async ({ reportId, status }: { reportId: string; status: ReportStatus }) =>
    callApi<ContentReport>({ url: `${adminApiUrl}/content-reports/${reportId}`, method: 'PATCH', data: { status } });

export const useUpdateContentReportStatus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateContentReportStatus,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['content-reports'] }),
    });
};
